import { Component, KeyboardEvent, createRef } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useGoogleMapsKey } from '../../hooks/useGoogleMapsKey';
import { localizacaoMapaSchema } from '../../validation/localizacaoMapaSchema';
import LocalizacaoMapaView from './LocalizacaoMapaView';

export interface LocalizacaoMapaEditorProps {
  urlLocalizacao?: string | null;
  onChange: (url: string) => void;
}

interface LocalizacaoMapaEditorImplProps extends LocalizacaoMapaEditorProps {
  hasKey: boolean;
  apiKey: string | null;
}

interface LocalizacaoMapaEditorState {
  error: string | null;
  placesError: string | null;
  center: { lat: number; lng: number };
}

// Centro genérico (São Paulo) até existir uma seleção real via Places.
const DEFAULT_CENTER = { lat: -23.5505, lng: -46.6333 };

/**
 * Deriva uma URL estável do Google Maps a partir do `PlaceResult` selecionado
 * no Autocomplete — prioriza endereço formatado + `place_id` (mais preciso
 * para reabrir o mesmo local depois); cai para "lat,lng" quando o endereço
 * formatado não está disponível. Formato "Maps URLs" oficial
 * (https://developers.google.com/maps/documentation/urls/get-started), não
 * inventado.
 */
function buildUrlFromPlace(
  place: google.maps.places.PlaceResult,
): string | null {
  const location = place.geometry?.location;
  const query =
    place.formatted_address ??
    place.name ??
    (location ? `${location.lat()},${location.lng()}` : null);

  if (!query) {
    return null;
  }

  const params = new URLSearchParams({ api: '1', query });
  if (place.place_id) {
    params.set('query_place_id', place.place_id);
  }
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

/**
 * Implementação como componente de CLASSE (não função) — ver o comentário
 * completo no `export default function LocalizacaoMapaEditor` abaixo, que
 * explica o motivo (compatibilidade com o padrão de teste
 * `jest.resetModules()` + `require()` dinâmico usado por
 * `LocalizacaoMapaEditor.test.tsx`).
 */
class LocalizacaoMapaEditorImpl extends Component<
  LocalizacaoMapaEditorImplProps,
  LocalizacaoMapaEditorState
> {
  state: LocalizacaoMapaEditorState = {
    error: null,
    placesError: null,
    center: DEFAULT_CENTER,
  };

  private inputRef = createRef<HTMLInputElement>();

  private addressInputRef = createRef<HTMLInputElement>();

  private autocomplete: google.maps.places.Autocomplete | null = null;

  private placeChangedListener: google.maps.MapsEventListener | null = null;

  componentWillUnmount() {
    this.placeChangedListener?.remove();
    this.autocomplete = null;
  }

  /**
   * NÃO é um submit de `<form>` — é um handler de clique/Enter. Este
   * componente é embutido em telas que JÁ possuem seu próprio `<form>`
   * (`LocalModal.tsx:186`, `AdminQuadrasDashboard.tsx:620`); renderizar um
   * `<form>` aqui criaria `<form>` aninhado, que o HTML proíbe. Na prática o
   * botão passava a pertencer ao form EXTERNO: clicar em "Salvar localização"
   * submetia o formulário da tela hospedeira e a validação Zod da URL nunca
   * rodava (regressão real capturada pelo e2e `localizacao-mapa.spec.ts`, não
   * pelo jsdom — que associa form-owner de forma diferente do Chrome).
   */
  private handleSubmit = () => {
    const value = this.inputRef.current?.value ?? '';
    const result = localizacaoMapaSchema.safeParse({ urlLocalizacao: value });

    if (!result.success) {
      this.setState({
        error: result.error.issues[0]?.message ?? 'URL inválida.',
      });
      return;
    }

    this.setState({ error: null });
    this.props.onChange(result.data.urlLocalizacao);
  };

  /**
   * Preserva a ergonomia que o `<form>` dava (Enter confirma) sem reintroduzir
   * o elemento. `preventDefault` impede que o Enter submeta o formulário da
   * tela hospedeira, que é justamente o acoplamento que removemos acima.
   */
  private handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSubmit();
    }
  };

  /**
   * DECISÃO DE WIDGET (Task 6; ver plano Risks "Widget de Places Autocomplete
   * em transição"): entre `PlaceAutocompleteElement` (Web Component novo,
   * recomendado pelo Google) e UI própria sobre `AutocompleteSuggestion`
   * (recomendado pelos mantenedores do @vis.gl/react-google-maps,
   * github.com/visgl/react-google-maps/discussions/707), esta fase adota o
   * `google.maps.places.Autocomplete` clássico: continua totalmente
   * suportado pela Maps JS API (não removido — só não é mais o recomendado
   * para código NOVO desde 2025), anexa-se a um único `<input>` já existente
   * e fornece a UI de sugestões PRONTA do próprio Google (nenhuma lista/
   * dropdown construída à mão aqui — "library/official-embed only"). É
   * carregado via `google.maps.importLibrary('places')` (API oficial e
   * assíncrona do Maps JS, documentada em
   * https://developers.google.com/maps/documentation/javascript/libraries),
   * chamada a partir do callback `onLoad` do `APIProvider` — não do hook
   * `useMapsLibrary` do @vis.gl/react-google-maps, porque este componente é
   * uma CLASSE (ver motivo no wrapper função abaixo) e hooks não podem ser
   * chamados fora de componentes função/hooks customizados. `onLoad` e
   * `importLibrary` são APIs simples (callback/Promise), não hooks — por
   * isso funcionam aqui sem reintroduzir o problema do dispatcher.
   */
  private handleMapsLoaded = async () => {
    const inputEl = this.addressInputRef.current;
    if (!inputEl) {
      return;
    }
    if (typeof google === 'undefined' || !google.maps?.importLibrary) {
      this.setState({
        placesError:
          'Não foi possível carregar a busca de endereço agora. Tente novamente mais tarde.',
      });
      return;
    }

    try {
      const { Autocomplete } = (await google.maps.importLibrary(
        'places',
      )) as google.maps.PlacesLibrary;

      const autocomplete = new Autocomplete(inputEl, {
        fields: ['formatted_address', 'name', 'place_id', 'geometry'],
      });
      this.autocomplete = autocomplete;
      this.placeChangedListener = autocomplete.addListener(
        'place_changed',
        this.handlePlaceChanged,
      );
      this.setState({ placesError: null });
    } catch {
      this.setState({
        placesError:
          'Não foi possível carregar a busca de endereço agora. Tente novamente mais tarde.',
      });
    }
  };

  private handlePlaceChanged = () => {
    const place = this.autocomplete?.getPlace();
    if (!place) {
      return;
    }

    const url = buildUrlFromPlace(place);
    if (!url) {
      this.setState({
        placesError: 'Não foi possível determinar a localização selecionada.',
      });
      return;
    }

    this.setState({ placesError: null });
    const location = place.geometry?.location;
    if (location) {
      this.setState({ center: { lat: location.lat(), lng: location.lng() } });
    }
    this.props.onChange(url);
  };

  render() {
    const { urlLocalizacao, hasKey, apiKey } = this.props;

    if (hasKey && apiKey) {
      return (
        <div>
          <APIProvider
            apiKey={apiKey}
            libraries={['places']}
            onLoad={this.handleMapsLoaded}
          >
            <Map
              center={this.state.center}
              defaultZoom={14}
              mapId="LOCALIZACAO_MAPA_EDITOR"
              style={{ width: '100%', height: 320 }}
            >
              <AdvancedMarker position={this.state.center} />
            </Map>
          </APIProvider>
          <div className="mb-3">
            <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">
              Buscar endereço
            </label>
            <input
              className="form-control rounded-xl font-xsss"
              placeholder="Buscar endereço..."
              ref={this.addressInputRef}
            />
            {this.state.placesError && (
              <p className="text-danger font-xssss mt-1 mb-0">
                {this.state.placesError}
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="mb-3">
          <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">
            URL de Localização
          </label>
          <input
            className="form-control rounded-xl font-xsss"
            placeholder="https://maps.google.com/..."
            defaultValue={urlLocalizacao ?? ''}
            ref={this.inputRef}
            onKeyDown={this.handleInputKeyDown}
          />
          {this.state.error && (
            <p className="text-danger font-xssss mt-1 mb-0">
              {this.state.error}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={this.handleSubmit}
          className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3"
        >
          Salvar localização
        </button>
        {/* Contexto de EDIÇÃO: aqui a explicação é acionável (o organizador
            pode trocar pela URL completa), então ligamos a flag — nas telas
            de visualização ela fica desligada e só resta o "Ver no mapa". */}
        <LocalizacaoMapaView
          urlLocalizacao={urlLocalizacao}
          explicarQuandoNaoEmbutivel
        />
      </div>
    );
  }
}

/**
 * Componente reutilizável de EDIÇÃO de localização (camada `components/`),
 * com dois modos decididos por `useGoogleMapsKey()` (plano
 * google-maps-localizacao-phase-2-frontend-fundao-de-mapa, Task 6):
 *
 * - SEM key (AC-A2/AC-A3): input de URL validado por Zod
 *   (`localizacaoMapaSchema`) + preview keyless reaproveitando
 *   `LocalizacaoMapaView`.
 * - COM key (AC-A4): scaffold oficial `APIProvider`+`Map`+`AdvancedMarker`
 *   (`@vis.gl/react-google-maps`, Pattern 6 do plano) + busca de endereço
 *   real via `google.maps.places.Autocomplete` (ver comentário em
 *   `handleMapsLoaded`, na classe acima) — ao selecionar um resultado,
 *   `onChange` é chamado com a `urlLocalizacao` derivada da seleção
 *   (`buildUrlFromPlace`), sem o usuário digitar a URL manualmente, e o
 *   mapa/marcador recentraliza no local escolhido.
 *
 * A implementação de fato (`LocalizacaoMapaEditorImpl`, acima) é um
 * componente de CLASSE, não de função — decisão de compatibilidade de
 * teste, não de arquitetura: `LocalizacaoMapaEditor.test.tsx` carrega este
 * módulo via `jest.resetModules()` + `require()` dinâmico por teste (para
 * não assumir se `useGoogleMapsKey` lê `process.env` em escopo de módulo ou
 * por chamada). Esse padrão invalida o dispatcher de HOOKS do React entre o
 * módulo recarregado e o `react-dom` já carregado no topo do arquivo de
 * teste (mesma raiz do clássico "Invalid hook call... more than one copy of
 * React" — confirmado experimentalmente: qualquer `useState`/`useRef`/
 * `useForm`/`useMapsLibrary` chamado dentro do módulo recarregado quebra em
 * runtime; um componente de classe não usa esse dispatcher, porque seu
 * ciclo de vida (`render`/`setState`) é implementado inteiramente pelo
 * react-dom via Fiber). Por isso toda a lógica com estado (erro de
 * validação, ref do input, Autocomplete de Places, centro do mapa) vive na
 * classe, usando apenas APIs não-hook (`onLoad` prop, `google.maps.
 * importLibrary`, `Autocomplete.addListener`) para a integração com Places.
 * Este wrapper função só existe para chamar `useGoogleMapsKey()` de um
 * local que respeita `eslint-plugin-react-hooks` (hooks só podem ser
 * chamados de componentes função/hooks customizados, nunca de métodos de
 * classe).
 */
export default function LocalizacaoMapaEditor(props: LocalizacaoMapaEditorProps) {
  const { hasKey, apiKey } = useGoogleMapsKey();
  return <LocalizacaoMapaEditorImpl {...props} hasKey={hasKey} apiKey={apiKey} />;
}
